// useReunion — the whole client state machine in one hook. It mirrors the
// prototype's Component (state + renderVals from Water Hazard.dc.html), but
// delegates data + auth to the selected Backend. Components stay presentational.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createBackend, type AuthStep, type Backend, type RosterData, type RosterHousehold } from '../services';
import type { Phase, ReunionForm, SectionKey, SiteContent } from '../types';

const NOTIF_MS = 9000;

interface UIState {
  ready: boolean;
  content: SiteContent | null;
  phaseOverride: Phase | null;
  auth: AuthStep;
  householdId: string | null;
  email: string;
  code: string;
  isAdmin: boolean;
  notListed: boolean;
  codeErr: '' | 'wrong' | 'expired';
  expired: boolean;
  submitted: boolean;
  form: ReunionForm;
  notif: boolean;
  adminOpen: boolean;
  devOpen: boolean;
  adminNote: string;
  newEmail: string;
  roster: RosterData | null;
  adminRows: RosterHousehold[];
  busy: boolean;
  // admin detail panel (view a household's answer / enter one for them)
  adminDetail: { hid: string; display: string; mode: 'view' | 'enter' } | null;
  adminForm: ReunionForm;
  adminBusy: boolean;
}

const EMPTY_FORM: ReunionForm = {
  weeks: [],
  adults: '2',
  kids: '0',
  ages: '',
  travel: '',
  budget: '',
  notes: '',
};

const INITIAL: UIState = {
  ready: false,
  content: null,
  phaseOverride: null,
  auth: 'email',
  householdId: null,
  email: '',
  code: '',
  isAdmin: false,
  notListed: false,
  codeErr: '',
  expired: false,
  submitted: false,
  form: EMPTY_FORM,
  notif: false,
  adminOpen: false,
  devOpen: false,
  adminNote:
    'one document per household. addresses are normalized before they are stored and before they are checked.',
  newEmail: '',
  roster: null,
  adminRows: [],
  busy: false,
  adminDetail: null,
  adminForm: EMPTY_FORM,
  adminBusy: false,
};

export function useReunion() {
  const [s, setS] = useState<UIState>(INITIAL);
  const backendRef = useRef<Backend | null>(null);
  const notifTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set only when the user just submitted (not when an already-submitted user
  // loads the page), so we scroll the confirmation into view exactly once.
  const justSubmitted = useRef(false);

  const patch = useCallback((p: Partial<UIState>) => setS((prev) => ({ ...prev, ...p })), []);

  const backend = () => backendRef.current!;

  const refreshRoster = useCallback(async () => {
    const roster = await backend().loadRoster();
    patch({ roster });
  }, [patch]);

  const showNotif = useCallback(() => {
    if (!backend().demoCode) return; // real backend sends a real email
    patch({ notif: true });
    if (notifTimer.current) clearTimeout(notifTimer.current);
    notifTimer.current = setTimeout(() => patch({ notif: false }), NOTIF_MS);
  }, [patch]);

  // --- bootstrap ---------------------------------------------
  useEffect(() => {
    let alive = true;
    (async () => {
      const b = await createBackend();
      backendRef.current = b;
      const [content, session, roster] = await Promise.all([
        b.loadContent(),
        b.loadSession(),
        b.loadRoster(),
      ]);
      if (!alive) return;
      patch({
        ready: true,
        content,
        roster,
        auth: session.auth,
        householdId: session.householdId,
        email: session.email,
        isAdmin: session.isAdmin,
        submitted: session.submitted,
        expired: session.expired,
      });
    })();
    return () => {
      alive = false;
      if (notifTimer.current) clearTimeout(notifTimer.current);
    };
  }, [patch]);

  // --- respond flow ------------------------------------------
  const onEmail = useCallback((v: string) => patch({ email: v, notListed: false }), [patch]);

  const sendCode = useCallback(async () => {
    if (s.busy) return;
    patch({ busy: true });
    try {
      const res = await backend().sendCode(s.email);
      if (res.notListed) {
        patch({ notListed: true });
        return;
      }
      patch({ notListed: false, auth: 'code', code: '', codeErr: '', expired: false });
      showNotif();
    } catch (e) {
      console.error('sendCode failed', e);
      patch({ notListed: false });
      alert("something went wrong sending your code. try again — or text me at (555) 214-8890.");
    } finally {
      patch({ busy: false });
    }
  }, [s.busy, s.email, patch, showNotif]);

  const verify = useCallback(async () => {
    if (s.busy) return;
    patch({ busy: true });
    try {
      const res = await backend().verifyCode(s.email, s.code);
      if (!res.ok) {
        patch({ codeErr: res.error });
        return;
      }
      const session = res.session!;
      patch({
        auth: 'in',
        householdId: session.householdId,
        email: session.email || s.email,
        isAdmin: session.isAdmin,
        submitted: session.submitted,
        codeErr: '',
        notif: false,
        expired: false,
      });
      await refreshRoster();
    } catch (e) {
      // A thrown call is a server/network problem, NOT a wrong code — don't
      // mislabel it "that's not it". Surface it plainly and let them retry.
      console.error('verify failed', e);
      alert("something went wrong checking your code — give it a moment and try again.");
    } finally {
      patch({ busy: false });
    }
  }, [s.busy, s.email, s.code, patch, refreshRoster]);

  const onCode = useCallback(
    (v: string) => patch({ code: v.replace(/\D/g, '').slice(0, 6), codeErr: '' }),
    [patch],
  );

  const resend = useCallback(async () => {
    await backend().resend(s.email);
    patch({ codeErr: '' });
    showNotif();
  }, [s.email, patch, showNotif]);

  const backToEmail = useCallback(() => patch({ auth: 'email', code: '', codeErr: '' }), [patch]);

  const signOut = useCallback(async () => {
    await backend().signOut();
    patch({
      auth: 'email',
      householdId: null,
      code: '',
      codeErr: '',
      submitted: false,
      expired: false,
      isAdmin: false,
      adminOpen: false,
    });
    await refreshRoster();
  }, [patch, refreshRoster]);

  const setForm = useCallback(
    (p: Partial<ReunionForm>) => setS((prev) => ({ ...prev, form: { ...prev.form, ...p } })),
    [],
  );

  const submit = useCallback(async () => {
    if (s.busy) return;
    patch({ busy: true });
    try {
      await backend().submitResponse(s.form);
      justSubmitted.current = true;
      patch({ submitted: true });
      await refreshRoster();
    } catch (e) {
      console.error('submit failed', e);
      alert("something went wrong saving your answers. try again — or text me at (555) 214-8890.");
    } finally {
      patch({ busy: false });
    }
  }, [s.busy, s.form, patch, refreshRoster]);

  // The tall form collapsing to the short confirmation leaves the viewport
  // scrolled past it — bring the "got it, that's you done" panel into view,
  // but only right after an actual submit (never on initial load).
  useEffect(() => {
    if (s.submitted && justSubmitted.current) {
      justSubmitted.current = false;
      requestAnimationFrame(() =>
        document.getElementById('respond')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      );
    }
  }, [s.submitted]);

  const editAgain = useCallback(() => patch({ submitted: false }), [patch]);

  // --- admin -------------------------------------------------
  const openAdmin = useCallback(async () => {
    patch({ adminOpen: true });
    const adminRows = await backend().listAdminRows();
    patch({ adminRows });
  }, [patch]);

  const closeAdmin = useCallback(() => patch({ adminOpen: false }), [patch]);
  const onNewEmail = useCallback((v: string) => patch({ newEmail: v }), [patch]);

  const addEmail = useCallback(async () => {
    const res = await backend().addAddress(s.newEmail);
    const adminRows = await backend().listAdminRows();
    patch({ adminNote: res.message, newEmail: res.ok ? '' : s.newEmail, adminRows });
  }, [s.newEmail, patch]);

  // Open the per-household detail panel: "view" (answered) shows their answer;
  // "enter for them" (waiting) opens the form to fill on their behalf.
  const openHouseholdDetail = useCallback(
    async (hid: string, display: string, responded: boolean) => {
      patch({ adminBusy: true, adminDetail: { hid, display, mode: responded ? 'view' : 'enter' } });
      try {
        const existing = await backend().getHouseholdResponse(hid);
        patch({ adminForm: existing ?? EMPTY_FORM });
      } catch (e) {
        console.error('load household response failed', e);
        patch({ adminForm: EMPTY_FORM });
      } finally {
        patch({ adminBusy: false });
      }
    },
    [patch],
  );

  const setAdminForm = useCallback(
    (p: Partial<ReunionForm>) => setS((prev) => ({ ...prev, adminForm: { ...prev.adminForm, ...p } })),
    [],
  );

  const editHouseholdDetail = useCallback(
    () => setS((prev) => (prev.adminDetail ? { ...prev, adminDetail: { ...prev.adminDetail, mode: 'enter' } } : prev)),
    [],
  );

  const closeHouseholdDetail = useCallback(() => patch({ adminDetail: null }), [patch]);

  const submitHouseholdDetail = useCallback(async () => {
    const d = s.adminDetail;
    if (!d || s.adminBusy) return;
    patch({ adminBusy: true });
    try {
      await backend().enterForHousehold(d.hid, s.adminForm);
      const [rows] = await Promise.all([backend().listAdminRows(), refreshRoster()]);
      // `responded` is set by an async trigger that can lag this refetch, so
      // reflect the save immediately for the row we just wrote.
      const adminRows = rows.map((r) => (r.id === d.hid ? { ...r, responded: true } : r));
      patch({ adminRows, adminDetail: null, adminNote: `saved ${d.display}'s answers` });
    } catch (e) {
      console.error('enterForHousehold failed', e);
      alert("couldn't save that — try again.");
    } finally {
      patch({ adminBusy: false });
    }
  }, [s.adminDetail, s.adminForm, s.adminBusy, patch, refreshRoster]);

  // --- dev pill ----------------------------------------------
  const toggleDev = useCallback(() => setS((prev) => ({ ...prev, devOpen: !prev.devOpen })), []);
  const pickPhase = useCallback((p: Phase) => patch({ phaseOverride: p, devOpen: false }), [patch]);

  const devReset = useCallback(async () => {
    await backend().debugReset?.();
    patch({
      auth: 'email',
      householdId: null,
      email: '',
      code: '',
      codeErr: '',
      notListed: false,
      submitted: false,
      expired: false,
      isAdmin: false,
      adminOpen: false,
      devOpen: false,
    });
    await refreshRoster();
  }, [patch, refreshRoster]);

  const devExpire = useCallback(async () => {
    await backend().debugExpire?.();
    patch({
      auth: 'email',
      householdId: null,
      expired: true,
      submitted: false,
      isAdmin: false,
      adminOpen: false,
      devOpen: false,
    });
  }, [patch]);

  const devAdmin = useCallback(() => {
    patch({ adminOpen: true, devOpen: false });
    void openAdmin();
  }, [patch, openAdmin]);

  // --- derived view model ------------------------------------
  const view = useMemo(() => {
    const content = s.content;
    if (!content) return null;
    const phase: Phase = s.phaseOverride ?? content.phase;
    const vis = (key: SectionKey) => (content.sections[key] || []).indexOf(phase) > -1;
    const signedIn = s.auth === 'in';

    const roster = s.roster;
    const households = roster?.households ?? null;
    const respondedHouseholds = (households ?? []).filter((h) => h.responded);
    const householdLabel =
      households?.find((h) => h.id === s.householdId)?.display ||
      (s.email ? s.email.split('@')[0] : '');

    const rosterNames = respondedHouseholds.map((h) => h.display).join(' · ');
    const waitingCount = households
      ? households.filter((h) => !h.responded).length
      : 0;

    const invited = roster?.invited ?? content.site.invited;
    const headsIn = roster?.headsIn ?? 0;

    // The decided date window (gated config/plan; only ever loaded signed in).
    // Once set, the "when" fact and the "exactly when" open question narrow.
    // Signed out, both stay at the vague public copy.
    const window = signedIn ? (roster?.plan?.window ?? null) : null;
    const facts = (content.known[phase] ?? []).map((f) =>
      window && f.k === 'when' ? { ...f, v: window.label, note: 'decided' } : f,
    );
    const open = content.open.map((item) =>
      window && item.decided ? { ...item, q: item.decided.q, a: item.decided.a } : item,
    );

    return {
      content,
      phase,
      vis,
      signedIn,
      window,
      facts,
      open,
      isAdmin: s.isAdmin,
      householdLabel,
      // respond steps
      expired: s.expired,
      stepEmail: !signedIn && s.auth === 'email',
      stepCode: !signedIn && s.auth === 'code',
      stepForm: signedIn && !s.submitted,
      stepDone: signedIn && s.submitted,
      notListed: s.notListed,
      codeErr: s.codeErr,
      // who's in
      headsIn,
      invited,
      rosterNames,
      waitingCount,
      // organizer contact — only loaded when signed in (gated config/contact)
      contact: roster?.contact ?? null,
      // toast
      showNotif: s.notif && !!backend().demoCode,
      demoCode: backend().demoCode,
      // dev
      devVisible: import.meta.env.VITE_SHOW_DEV_PANEL !== 'false' && !!backend().demoCode,
    };
  }, [s]);

  return {
    ready: s.ready,
    state: s,
    view,
    actions: {
      onEmail,
      sendCode,
      onCode,
      verify,
      resend,
      backToEmail,
      signOut,
      setForm,
      submit,
      editAgain,
      openAdmin,
      closeAdmin,
      onNewEmail,
      addEmail,
      openHouseholdDetail,
      closeHouseholdDetail,
      editHouseholdDetail,
      setAdminForm,
      submitHouseholdDetail,
      toggleDev,
      pickPhase,
      devReset,
      devExpire,
      devAdmin,
    },
  };
}

export type ReunionApi = ReturnType<typeof useReunion>;
